import { useHistory, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * アプリケーション全体で統一されたナビゲーション管理を提供するカスタムフック。
 * このアプリケーションの戻るボタンの挙動パターンを調べ、それらをシンプルなメソッドで使用できるようにラップした（Facadeパターン）。
 * React Router v5の古いナビゲーション関連のフックをこのフックの中にカプセル化している。v6に移行する場合はこのフック内だけを修正すれば良い状態にできると理想的。
 * 
 * このフックを使用することで、以下のようなメリットがある。
 * 1. 状態をURLクエリパラメータとして保存しながら遷移できる。
 * 2. 安全に戻ることができる。
 * 3. 既存のクエリパラメータを保持したまま別のパスに遷移できる。
 * 4. 戻り先のパスを取得できる。
 * 5. 通知画面からの遷移かどうかを判定できる。
 * 6. 通知経由の場合は通知画面に、そうでない場合はfallbackPathに戻る。
 * 7. クエリパラメータの取得・操作を簡単に行える。
 * 
 * 使用例:
 * ```typescript
 * const { navigateWithState, goBackSafely, getReturnPath, getQueryParam } = useAppNavigation();
 * 
 * // 状態をURLクエリパラメータとして保存しながら遷移（履歴に追加）
 * navigateWithState('/vehicles', { categoryId: 1, subCategoryId: 2 });
 * 
 * // 状態をURLクエリパラメータとして保存しながら遷移（履歴を置き換え）
 * // 使い所としては画面遷移を伴わない、状態変化を記録する場合（フィルター条件の変更など）
 * navigateWithState('/vehicles', { categoryId: 1, subCategoryId: 2 }, 'replace');
 * 
 * // 自動判定モード（推奨）
 * // サイドメニューから来た直後の初回操作は履歴に残し、それ以降は履歴を置き換える
 * navigateWithState('/vehicles', { categoryId: 1, subCategoryId: 2 }, 'auto');
 * 
 * // 安全に戻る（遷移元情報があれば使用、なければフォールバック先へ）
 * goBackSafely('/home');
 * 
 * // クエリパラメータの取得
 * const categoryId = getQueryParam('categoryId');
 * ```
 */
export const useAppNavigation = () => {
  const history = useHistory();
  const location = useLocation();

  /**
   * 状態をURLクエリパラメータとして保存しながら遷移
   * これにより、ブラウザの戻るボタンやリロード時も状態が保持される
   * 
   * @param path - 遷移先のパス
   * @param queryParams - URLクエリパラメータとして追加するオブジェクト
   * @param method - 'push'で履歴に追加、'replace'で履歴を置き換え、'auto'で自動判定（デフォルト: 'push'）
   *                'auto'の場合、サイドメニューから来た直後の初回操作はpush、それ以降はreplace
   */
  const navigateWithState = useCallback((
    path: string, 
    queryParams?: Record<string, any>, 
    method: 'push' | 'replace' | 'auto' = 'push'
  ) => {
    const targetPath = queryParams ? (() => {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.set(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      return `${path}${queryString ? `?${queryString}` : ''}`;
    })() : path;

    let actualMethod: 'push' | 'replace' = method === 'auto' ? 'replace' : method;
    
    if (method === 'auto') {
      // 現在のURLにfromパラメータがあり、遷移先にfromパラメータがない場合はpush（履歴を残す）
      const currentParams = new URLSearchParams(location.search);
      const hasFromParam = currentParams.has('from');
      const targetHasFromParam = targetPath.includes('from=');
      
      if (hasFromParam && !targetHasFromParam) {
        // サイドメニューから来た直後の初回操作は履歴に残す
        actualMethod = 'push';
      } else {
        // それ以外は履歴を置き換える（何度も戻るボタンを押さなくて済む）
        actualMethod = 'replace';
      }
    }

    // 現在のパスと遷移先のパスが同じ場合は履歴を置き換える
    // イベントが2度発火するケースがあったため対応
    const currentPathWithSearch = `${location.pathname}${location.search}`;
    if (actualMethod === 'push' && targetPath === currentPathWithSearch) {
      actualMethod = 'replace';
    }

    if (actualMethod === 'replace') {
      history.replace(targetPath);
    } else {
      history.push(targetPath);
    }
  }, [history, location.search]);

  /**
   * 現在のURLから特定のクエリパラメータを取得
   */
  const getQueryParam = useCallback((key: string): string | null => {
    const params = new URLSearchParams(location.search);
    return params.get(key);
  }, [location.search]);

  /**
   * 現在のURLから全てのクエリパラメータをオブジェクトとして取得
   */
  const getAllQueryParams = useCallback((): Record<string, string> => {
    const params = new URLSearchParams(location.search);
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }, [location.search]);

  /**
   * 安全に前のページに戻る
   * location.stateに遷移元情報があればそれを使用、なければfallbackPathへ遷移
   * 
   * @param fallbackPath - 遷移元情報がない場合の戻り先（デフォルトは現在のパス）
   */
  const goBackSafely = useCallback((fallbackPath?: string) => {
    // URLクエリパラメータに'from'が指定されている場合はそれを優先
    const fromQuery = getQueryParam('from');
    if (fromQuery) {
      history.push(fromQuery);
      return;
    }

    // location.stateに遷移元情報がある場合はそれを使用
    const state = location.state as any;
    if (state?.from) {
      history.push(state.from);
      return;
    }

    // 履歴がある場合は戻る
    if (history.length > 1) {
      history.goBack();
      return;
    }

    // どれも該当しない場合はfallbackPathへ（指定がなければ何もしない）
    if (fallbackPath) {
      history.push(fallbackPath);
    }
  }, [history, location.state, getQueryParam]);

  /**
   * 通知画面からの遷移かどうかを判定
   * URLパラメータに'noti'があるかチェック
   */
  const isFromNotification = useCallback((): boolean => {
    return getQueryParam('noti') !== null;
  }, [getQueryParam]);

  /**
   * 通知経由の場合は通知画面に、そうでない場合はfallbackPathに戻る
   */
  const goBackFromDetail = useCallback((fallbackPath: string) => {
    if (isFromNotification()) {
      history.push('/notification');
    } else {
      goBackSafely(fallbackPath);
    }
  }, [history, isFromNotification, goBackSafely]);

  /**
   * 現在のクエリパラメータを保持したまま別のパスに遷移
   * 
   * @param path - 遷移先のパス
   * @param additionalParams - 追加するクエリパラメータ
   * @param method - 'push'で履歴に追加、'replace'で履歴を置き換え、'auto'で自動判定（デフォルト: 'push'）
   */
  const navigatePreservingQuery = useCallback((
    path: string, 
    additionalParams?: Record<string, any>, 
    method: 'push' | 'replace' | 'auto' = 'push'
  ) => {
    const currentParams = getAllQueryParams();
    const mergedParams = { ...currentParams, ...additionalParams };
    navigateWithState(path, mergedParams, method);
  }, [getAllQueryParams, navigateWithState]);

  /**
   * 戻り先のパスを取得（実際には遷移しない）
   * テストやログ出力に使用
   */
  const getReturnPath = useCallback((): string => {
    const fromQuery = getQueryParam('from');
    if (fromQuery) return fromQuery;

    const state = location.state as any;
    if (state?.from) return state.from;

    return 'history.goBack()';
  }, [location.state, getQueryParam]);

  return {
    navigateWithState,
    getQueryParam,
    getAllQueryParams,
    goBackSafely,
    isFromNotification,
    goBackFromDetail,
    navigatePreservingQuery,
    getReturnPath,
    // 現在のパスとクエリを値として公開（コンポーネント側でlocation依存を避けるため）
    currentPath: location.pathname,
    currentQueryString: location.search,
    // 既存のhistoryオブジェクトも公開（互換性のためだが、できれば避けるべき）
    history,
    location,
  };
};

